import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Input } from 'components/atoms/index';
import { ProgressBar } from 'components/atoms/ProgressBar/ProgressBar';
import MiniProgressBar from 'components/atoms/ProgressBar/MiniProgressBar';
import { Icon } from '@/icons';
import { vars } from 'style/theme.css';
import { studentHomeworkApi, adminTextbookApi } from '@/services/api';
import { getSlugPrefix } from '@/utils/slug';
import { formatNumber } from '@/utils/number';
import catImg from '@/assets/img/cat_test_img.png';

interface LearningItem {
    id: number;
    title: string;
    startDate: string;
    endDate: string;
    period: string;
    status: 'completed' | 'in-progress' | 'waiting';
    progress: number;
    daysUntilEnd: number;
    backgroundColor?: string;
    borderColor?: string;
    maxUnitsPerDay?: number;
    scoringRequired?: boolean;
    thumbnailUrl?: string;
}

interface TextbookPreview {
    id: number;
    title: string;
    unitCount: number;
    progress: number;
    thumbnailUrl?: string;
    unitSummary?: string;
}

const MAX_VISIBLE_UNIT_LABELS = 3;

const toUnitDisplayLabel = (unit: any): string | null => {
    if (!unit) return null;
    const rawTitle = unit?.unitTitle ?? unit?.title ?? unit?.name;
    const rawCode = unit?.unitCode ?? unit?.code;
    const title = typeof rawTitle === 'string' ? rawTitle.trim() : rawTitle != null ? String(rawTitle).trim() : '';
    const code = typeof rawCode === 'string' ? rawCode.trim() : rawCode != null ? String(rawCode).trim() : '';
    if (title) return title;
    if (code) return code;
    return null;
};

const summarizeUnitLabels = (labels: string[], fallbackCount?: number) => {
    const deduped: string[] = [];
    labels.forEach((label) => {
        if (label && !deduped.includes(label)) {
            deduped.push(label);
        }
    });
    if (!deduped.length) {
        return typeof fallbackCount === 'number' ? `Unit ${formatNumber(fallbackCount)}` : '';
    }
    if (deduped.length <= MAX_VISIBLE_UNIT_LABELS) {
        return deduped.join(', ');
    }
    return `${deduped.slice(0, MAX_VISIBLE_UNIT_LABELS).join(', ')} 외 ${deduped.length - MAX_VISIBLE_UNIT_LABELS}개`;
};

const summarizeUnits = (units: any[], fallbackCount?: number) => {
    if (!Array.isArray(units)) {
        return typeof fallbackCount === 'number' ? `Unit ${formatNumber(fallbackCount)}` : '';
    }
    const labels = units
        .map((unit) => toUnitDisplayLabel(unit))
        .filter((label): label is string => Boolean(label));
    return summarizeUnitLabels(labels, fallbackCount);
};

// 학습 리스트
export default function StudentLearningList() {
    const navigate = useNavigate();
    const location = useLocation();
    const slugPrefix = getSlugPrefix(location.pathname);
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [learningList, setLearningList] = useState<LearningItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [textbooksMap, setTextbooksMap] = useState<Record<number, TextbookPreview[]>>({});
    const [scheduleThumbMap, setScheduleThumbMap] = useState<Record<number, string | undefined>>({});
    const coverCacheRef = useRef<Record<number, string>>({});
    const hasAdminAccess = () => typeof window !== 'undefined' && !!localStorage.getItem('admin_token');

    const formatPeriod = (start?: string, end?: string) => {
        const startLabel = start ? dayjs(start).format('YYYY.MM.DD') : '';
        const endLabel = end ? dayjs(end).format('YYYY.MM.DD') : '';
        if (startLabel && endLabel) return `${startLabel} ~ ${endLabel}`;
        if (endLabel) return `~ ${endLabel}`;
        if (startLabel) return `${startLabel} ~`;
        return '-';
    };

    const getStatusBadge = (status: LearningItem['status']) => {
        switch (status) {
            case 'completed':
                return { text: '학습완료', className: 'text-green-600 bg-green-100' };
            case 'waiting':
                return { text: '학습대기', className: 'text-blue-600 bg-blue-100' };
            default:
                return { text: '학습중', className: 'text-orange-600 bg-orange-100' };
        }
    };
    const getTextbookStatusBadge = (progress?: number | null) => {
        const value = Number(progress ?? 0);
        if (Number.isNaN(value) || value <= 0) {
            return { text: '학습대기', className: 'text-blue-600 bg-blue-100' };
        }
        if (value >= 100) {
            return { text: '학습완료', className: 'text-green-600 bg-green-100' };
        }
        return { text: '학습중', className: 'text-orange-600 bg-orange-100' };
    };

    const findImageDeep = (obj: any): string | undefined => {
        if (!obj || typeof obj !== 'object') return undefined;
        for (const [k, v] of Object.entries(obj)) {
            if (typeof v === 'string') {
                const key = String(k).toLowerCase();
                if (
                    key === 'imageurl' ||
                    key.endsWith('imageurl') ||
                    key === 'wordimageurl' ||
                    key.endsWith('wordimageurl') ||
                    key === 'thumbnailurl' ||
                    key.endsWith('thumbnailurl') ||
                    key === 'coverurl' ||
                    key.endsWith('coverurl')
                ) {
                    if (v.startsWith('http')) return v;
                }
            } else if (typeof v === 'object' && v) {
                const found = findImageDeep(v);
                if (found) return found;
            }
        }
        return undefined;
    };
    const getUnitImage = (u: any): string | undefined => {
        const quick =
            u?.textbookThumbnailUrl || u?.textbookThumbnailURL ||
            u?.textbookCoverUrl || u?.textbookCoverURL ||
            u?.textbookImageUrl || u?.textbookImageURL ||
            u?.bookImageUrl || u?.bookImageURL ||
            u?.thumbnailUrl || u?.coverUrl || u?.imageUrl || '';
        if (quick) return String(quick);
        const acts = Array.isArray(u?.activities) ? u.activities : [];
        for (const act of acts) {
            const items = Array.isArray(act?.items) ? act.items : [];
            for (const it of items) {
                const cj = it?.contentJson;
                if (typeof cj === 'string') {
                    try {
                        const parsed = JSON.parse(cj);
                        const found = findImageDeep(parsed);
                        if (found) return found;
                    } catch {}
                } else if (cj && typeof cj === 'object') {
                    const found = findImageDeep(cj);
                    if (found) return found;
                }
            }
        }
        return undefined;
    };
    // 학습 목록 조회
    const fetchLearningList = async () => {
        setIsLoading(true);
        setError('');
        
        try {
            const allowAdminApis = hasAdminAccess();
            const response = await studentHomeworkApi.learningList({
                search: searchTerm,
                onlyAvailable: showOnlyAvailable
            });

            if (response.success) {
                const list = response.data.list || [];
                setLearningList(list);
                const thumbMap: Record<number, string | undefined> = {};
                list.forEach((it: any) => {
                    const sid = Number(it?.id ?? it?.scheduleId ?? it?.scheduleID ?? 0);
                    if (sid > 0) {
                        thumbMap[sid] = it?.thumbnailUrl || it?.coverUrl || it?.imageUrl || undefined;
                    }
                });
                setScheduleThumbMap(thumbMap);
                const ids = list
                    .map((it: any) => Number(it?.id ?? it?.scheduleId ?? it?.scheduleID ?? 0))
                    .filter((n: any) => !Number.isNaN(n) && n > 0);
                if (ids.length) {
                    try {
                        await Promise.all(ids.map(async (scheduleId: number) => {
                            try {
                                const det = await studentHomeworkApi.detail(scheduleId);
                                const raw = det?.data || det;
                                const tbs = Array.isArray(raw?.textbooks) ? raw.textbooks : [];
                                const tbCount = tbs.length;
                                let mapped = (await Promise.all(tbs.map(async (tb: any) => {
                                    const units = Array.isArray(tb?.units) ? tb.units : [];
                                    const total = units.length;
                                    const unitSummary = summarizeUnits(units, total);
                                    const sum = units.reduce((acc: number, u: any) => {
                                        const st = String(u?.status || '').toUpperCase();
                                        const p = u?.progress != null ? Number(u.progress)
                                            : (st === 'COMPLETED' ? 100 : st === 'IN_PROGRESS' ? 50 : 0);
                                        return acc + (Number.isFinite(p) ? p : 0);
                                    }, 0);
                                    const prog = total > 0 ? Math.round(sum / total) : 0;
                                    let thumb = tb?.thumbnailUrl || tb?.thumbnailURL || tb?.coverUrl || tb?.coverURL || tb?.coverImageUrl || tb?.imageUrl || tb?.imageURL || tb?.bookImageUrl || tb?.iconUrl || '';
                                    
                                    // 교재 ID로 표지 이미지 가져오기
                                    const textbookId = Number(tb?.textbookId ?? tb?.textbookID ?? tb?.id ?? 0);
                                    if (allowAdminApis && !thumb && textbookId > 0) {
                                        // 캐시 확인
                                        if (coverCacheRef.current[textbookId]) {
                                            thumb = coverCacheRef.current[textbookId];
                                        } else {
                                            try {
                                                const detail = await adminTextbookApi.detail(textbookId);
                                                const coverUrl = detail?.data?.coverUrl || detail?.coverUrl || '';
                                                if (coverUrl) {
                                                    thumb = coverUrl;
                                                    coverCacheRef.current[textbookId] = coverUrl;
                                                }
                                            } catch {}
                                        }
                                    }
                                    
                                    // 교재 ID가 없고 유닛이 있으면 유닛으로 교재 찾기
                                    if (allowAdminApis && !thumb && !textbookId && total > 0) {
                                        for (const u of units) {
                                            const unitId = Number(u?.unitId ?? u?.id ?? 0);
                                            if (unitId > 0) {
                                                try {
                                                    const textbooksRes = await adminTextbookApi.findByUnit(unitId);
                                                    const textbooks = textbooksRes?.data || textbooksRes || [];
                                                    if (Array.isArray(textbooks) && textbooks.length > 0) {
                                                        const tbId = Number(textbooks[0]?.id ?? 0);
                                                        if (tbId > 0) {
                                                            if (coverCacheRef.current[tbId]) {
                                                                thumb = coverCacheRef.current[tbId];
                                                                break;
                                                            }
                                                            const detail = await adminTextbookApi.detail(tbId);
                                                            const coverUrl = detail?.data?.coverUrl || detail?.coverUrl || '';
                                                            if (coverUrl) {
                                                                thumb = coverUrl;
                                                                coverCacheRef.current[tbId] = coverUrl;
                                                                break;
                                                            }
                                                        }
                                                    }
                                                } catch {}
                                            }
                                        }
                                    }
                                    
                                    // 마지막 폴백: 교재가 1권만 있을 때에 한해 스케줄 대표이미지 사용
                                    if (!thumb && tbCount === 1) {
                                        thumb = thumbMap[scheduleId] || '';
                                    }
                                    return {
                                        id: textbookId,
                                        title: String(tb?.textbookTitle ?? tb?.textbookName ?? tb?.title ?? tb?.name ?? ''),
                                        unitCount: total,
                                        progress: prog,
                                        thumbnailUrl: thumb || undefined,
                                        unitSummary,
                                    };
                                }))).filter((tb: any) => (tb.id > 0 || tb.title));
                                // Fallback: textbooks가 비거나 없는 경우 unit 배열에서 교재 추론
                                if (!mapped.length) {
                                    const unitArr = Array.isArray(raw?.units) ? raw.units
                                        : (Array.isArray(raw?.unitList) ? raw.unitList : []);
                                    const groupMap = new Map<string, {
                                        id: number;
                                        title: string;
                                        unitCount: number;
                                        progressSum: number;
                                        progressCnt: number;
                                        thumbnailUrl?: string;
                                        firstUnitId?: number;
                                        unitLabels: string[];
                                    }>();
                                        unitArr.forEach((u: any) => {
                                            const uid = Number(u?.unitId ?? u?.id ?? 0);
                                        const tidNum = Number(u?.textbookId ?? u?.textbookID ?? u?.tbId ?? 0);
                                        const tTitle = String(u?.textbookTitle ?? u?.textbookName ?? u?.tbTitle ?? '');
                                        // 그룹화 시에는 스케줄 대표 이미지를 폴백으로 사용하지 않음(여러 교재 동일 이미지 방지)
                                        const tThumb = u?.textbookThumbnailUrl || u?.textbookThumbnailURL ||
                                                       u?.textbookCoverUrl || u?.textbookCoverURL ||
                                                       u?.textbookImageUrl || u?.textbookImageURL ||
                                                       u?.bookImageUrl || u?.bookImageURL ||
                                                       u?.thumbnailUrl || u?.coverUrl || u?.imageUrl || undefined;
                                        const key = tidNum > 0 ? `id:${tidNum}` : (tTitle ? `title:${tTitle}` : `unknown`);
                                            if (!groupMap.has(key)) {
                                                groupMap.set(key, {
                                                    id: tidNum > 0 ? tidNum : 0,
                                                    title: tTitle || '교재 미지정',
                                                    unitCount: 0,
                                                    progressSum: 0,
                                                    progressCnt: 0,
                                                    thumbnailUrl: tThumb,
                                                    firstUnitId: uid || undefined,
                                                    unitLabels: [],
                                                });
                                        }
                                        const rec = groupMap.get(key)!;
                                        rec.unitCount += 1;
                                        const st = String(u?.status || '').toUpperCase();
                                        const p = u?.progress != null ? Number(u.progress)
                                            : (st === 'COMPLETED' ? 100 : st === 'IN_PROGRESS' ? 50 : 0);
                                        rec.progressSum += Number.isFinite(p) ? p : 0;
                                        rec.progressCnt += 1;
                                        if (!rec.thumbnailUrl && tThumb) rec.thumbnailUrl = tThumb;
                                            if (!rec.firstUnitId && uid) rec.firstUnitId = uid;
                                            const unitLabel = toUnitDisplayLabel(u);
                                            if (unitLabel) {
                                                rec.unitLabels.push(unitLabel);
                                            }
                                    });
                                    const draft = Array.from(groupMap.values());
                                    // 교재 ID로 표지 이미지 가져오기
                                    await Promise.all(draft.map(async (v) => {
                                        if (!v.thumbnailUrl) {
                                            // 교재 ID가 있으면 직접 조회
                                            if (allowAdminApis && v.id > 0) {
                                                if (coverCacheRef.current[v.id]) {
                                                    v.thumbnailUrl = coverCacheRef.current[v.id];
                                                } else {
                                                    try {
                                                        const detail = await adminTextbookApi.detail(v.id);
                                                        const coverUrl = detail?.data?.coverUrl || detail?.coverUrl || '';
                                                        if (coverUrl) {
                                                            v.thumbnailUrl = coverUrl;
                                                            coverCacheRef.current[v.id] = coverUrl;
                                                        }
                                                    } catch {}
                                                }
                                            }
                                            // 교재 ID가 없으면 유닛으로 교재 찾기
                                            else if (allowAdminApis && v.firstUnitId) {
                                                try {
                                                    const textbooksRes = await adminTextbookApi.findByUnit(v.firstUnitId);
                                                    const textbooks = textbooksRes?.data || textbooksRes || [];
                                                    if (Array.isArray(textbooks) && textbooks.length > 0) {
                                                        const tbId = Number(textbooks[0]?.id ?? 0);
                                                        if (tbId > 0) {
                                                            v.id = tbId; // 교재 ID 업데이트
                                                            if (coverCacheRef.current[tbId]) {
                                                                v.thumbnailUrl = coverCacheRef.current[tbId];
                                                            } else {
                                                                const detail = await adminTextbookApi.detail(tbId);
                                                                const coverUrl = detail?.data?.coverUrl || detail?.coverUrl || '';
                                                                if (coverUrl) {
                                                                    v.thumbnailUrl = coverUrl;
                                                                    coverCacheRef.current[tbId] = coverUrl;
                                                                }
                                                            }
                                                        }
                                                    }
                                                } catch {}
                                            }
                                        }
                                    }));
                                    mapped = draft.map(v => ({
                                            id: v.id,
                                            title: v.title,
                                            unitCount: v.unitCount,
                                            progress: v.progressCnt > 0 ? Math.round(v.progressSum / v.progressCnt) : 0,
                                            thumbnailUrl: v.thumbnailUrl,
                                            unitSummary: summarizeUnitLabels(v.unitLabels, v.unitCount),
                                        }));
                                }
                                setTextbooksMap(prev => ({ ...prev, [scheduleId]: mapped }));
                            } catch {
                                setTextbooksMap(prev => ({ ...prev, [scheduleId]: [] }));
                            }
                        }));
                    } catch {}
                }
            } else {
                setError(response.message || '학습 목록을 불러오는데 실패했습니다.');
            }
        } catch (err) {
            console.error('학습 목록 조회 에러:', err);
            setError('학습 목록을 불러오는 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 필터/검색어 변경 시 디바운스 조회 (중복 호출 방지)
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLearningList();
        }, 300);
        return () => clearTimeout(timer);
    }, [showOnlyAvailable, searchTerm]);

    const handleToggleAvailable = () => {
        setShowOnlyAvailable(!showOnlyAvailable);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const clampProgress = (value?: number | null) => {
        if (!Number.isFinite(Number(value))) return 0;
        const num = Math.round(Number(value));
        return Math.max(0, Math.min(100, num));
    };

    const getScheduleProgress = (scheduleId: number, fallback?: number) => {
        const textbooks = textbooksMap[scheduleId];
        if (Array.isArray(textbooks) && textbooks.length > 0) {
            const valid = textbooks
                .map(tb => Number(tb?.progress))
                .filter((v) => Number.isFinite(v));
            if (valid.length > 0) {
                const sum = valid.reduce((acc, cur) => acc + cur, 0);
                return clampProgress(sum / valid.length);
            }
        }
        return clampProgress(fallback);
    };

return (    
    <main className='flex flex-1 justify-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
        <div className='flex flex-col w-full'>
            <h1 className='px-5 pt-5 lg:pb-0 heading1 lg:pt-0'>학습</h1>
                
            <section className='flex flex-col gap-2 items-start px-5 pt-3 w-full lg:flex-row lg:justify-between lg:items-center'>
                <Input
                    placeholder='검색어를 입력해주세요.'
                    className='max-w-[500px]'
                    value={searchTerm}
                    onChange={handleSearchChange}
                    icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                />
                <button 
                    className={`flex gap-1 justify-center items-center text-sm ${showOnlyAvailable ? 'text-indigo-500' : 'text-gray-500'}`}
                    onClick={handleToggleAvailable}
                >
                    <Icon name="check" size={20} className={showOnlyAvailable ? '*:fill-indigo-500' : '*:fill-gray-400'} />
                    <span>응시 가능한 학습만 보기</span>
                </button>
            </section>

            <div className='flex flex-col flex-1 gap-4 px-5 py-3'>
                {isLoading && (
                    <div className='flex flex-1 justify-center items-center py-10 text-center text-gray-500'>
                        학습 목록을 불러오는 중...
                    </div>
                )}

                {!isLoading && error && (
                    <div className='flex flex-1 justify-center items-center py-10 text-center text-red-500'>
                        {error}
                    </div>
                )}

                {!isLoading && !error && learningList.length === 0 && (
                    <div className='flex flex-1 justify-center items-center py-10 text-center text-gray-500'>
                        {showOnlyAvailable 
                            ? '현재 응시 가능한 학습이 없습니다.' 
                            : '배정된 학습이 없습니다.'}
                    </div>
                )}

                {!isLoading && !error && learningList.length > 0 && learningList.map((learning) => {
                    const scheduleId = Number((learning as any)?.id ?? (learning as any)?.scheduleId ?? 0);
                    const overallProgress = getScheduleProgress(scheduleId, learning.progress);
                    return (
                    <div key={learning.id} className='overflow-hidden card !rounded-lg border border-gray-100'>
                        <section className='flex justify-between items-center px-5 py-3 back_gra_ravendar'>
                            <p className='text-lg font-bold'>{learning.title}</p>
                            <div className="flex items-center gap-2">
                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                    learning.status === 'completed' ? 'text-green-600 bg-green-100'
                                    : learning.status === 'waiting' ? 'text-blue-600 bg-blue-100'
                                    : 'text-orange-600 bg-orange-100'
                                }`}>
                                    {learning.status === 'completed' ? '학습완료' : learning.status === 'waiting' ? '학습대기' : '학습중'}
                                </span>
                                <MiniProgressBar value={overallProgress} width={50} height={10} />
                            </div>
                        </section>
                        {(() => {
                            const tbs = textbooksMap[scheduleId];
                            if (tbs === undefined) {
                                return <div className='p-4 text-sm text-gray-500'>교재 정보를 불러오는 중...</div>;
                            }
                            if (!tbs.length) {
                                return <div className='p-4 text-sm text-gray-500'>표시할 교재가 없습니다.</div>;
                            }
                            const periodLabel = formatPeriod(learning.startDate, learning.endDate);
                            return (
                                <ul className='grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3'>
                                    {tbs.map((tb, idx) => {
                                        const badge = getTextbookStatusBadge(tb.progress);
                                        return (
                                            <li key={`${scheduleId}-${tb.id || tb.title || idx}`}>
                                                <div
                                                    className='flex flex-col gap-3 p-3 rounded-2xl border border-gray-100 shadow-sm bg-white cursor-pointer hover:shadow-md transition-shadow'
                                                    onClick={() =>
                                                        navigate(
                                                            tb.id > 0
                                                                ? `${slugPrefix}/learning/detail/${scheduleId}?textbookId=${tb.id}`
                                                                : `${slugPrefix}/learning/detail/${scheduleId}?textbookTitle=${encodeURIComponent(tb.title)}`
                                                        )
                                                    }
                                                >
                                                    <div className='w-full aspect-[4/3] rounded-2xl bg-gray-100 flex items-center justify-center overflow-hidden'>
                                                        {tb.thumbnailUrl ? (
                                                            <img
                                                                src={tb.thumbnailUrl}
                                                                alt={tb.title}
                                                                className='w-full h-full object-cover'
                                                                onError={(e) => {
                                                                    (e.currentTarget as HTMLImageElement).src = catImg;
                                                                }}
                                                            />
                                                        ) : (
                                                            <span className='text-xs text-gray-400'>교재 IMG</span>
                                                        )}
                                                    </div>
                                                    <div className='flex flex-col gap-2 px-1 pb-1'>
                                                        <div className='flex items-start justify-between gap-2'>
                                                            <div className='flex flex-1 flex-col gap-1 min-w-0'>
                                                                <p className='truncate text-sm font-semibold text-gray-900'>
                                                                    {tb.title || '교재 미지정'}
                                                                </p>
                                                                <p className='text-xs text-gray-400 whitespace-normal'>
                                                                    {tb.unitSummary || `(Unit ${formatNumber(tb.unitCount ?? 0)})`}
                                                                </p>
                                                            </div>
                                                            <span className={`text-[11px] font-semibold px-2 py-1 rounded-full shrink-0 ${badge.className}`}>
                                                                {badge.text}
                                                            </span>
                                                        </div>
                                                        <ProgressBar progress={tb.progress} size='small' showLabel />
                                                        <p className='text-xs text-gray-400'>학습기간 {periodLabel}</p>
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            );
                        })()}
                    </div>
                )})}
            </div>
        </div>
    </main>
  )
}